<template>
  <div
    v-show="visible"
    class="nui-dialog"
    :style="dialogStyle"
    @pointerdown.capture="bringToFront"
  >
    <div
      class="nui-dialog__header"
      :class="{ 'is-draggable': draggable }"
      @pointerdown="onHeaderPointerdown"
      @dblclick="onHeaderDblclick"
    >
      <slot name="header">
        <i v-if="icon" class="nui-dialog__icon" :class="icon"></i>
        <span class="nui-dialog__title">{{ title }}</span>
      </slot>
    </div>

    <div class="nui-dialog__body">
      <slot v-if="visible || !destroyOnClose"></slot>
    </div>

    <div v-if="$scopedSlots.footer" class="nui-dialog__footer">
      <slot name="footer"></slot>
    </div>

    <button
      v-if="showClose"
      type="button"
      class="nui-dialog__close"
      @click="close"
    >
      <i class="bx bx-x"></i>
    </button>

    <template v-if="resizable">
      <span
        v-for="d in directions"
        :key="d"
        class="nui-dialog__resizer"
        :class="'is-' + d"
        @pointerdown="startPointer($event, d)"
      ></span>
      <svg class="nui-dialog__resize-icon" viewBox="0 0 24 24">
        <path d="M10 20H20V10M12 17H17V12" />
      </svg>
    </template>
  </div>
</template>

<script>
import { PopupManager } from "element-ui/lib/utils/popup";

// 每位使用者一筆：asus_chat_dialog_{userId} = { [dialogId]: { x, y, width, height } }
const STORAGE_PREFIX = "asus_chat_dialog_";
// 關閉鈕位於彈窗下方外側，計算可視範圍時要預留：
// $nui-dialog-close-offset(15) + $nui-dialog-close-size(34) + 陰影餘裕(3)，修改 token 時需同步
const CLOSE_SPACE = 52;
// header 內這些元素不觸發拖曳與雙擊還原
const NO_DRAG_SELECTOR =
  "button, input, textarea, select, a, [contenteditable], .nui-dialog__no-drag";
// 開啟中的彈窗，Esc 只關閉最上層
const openDialogs = [];

const clamp = (val, min, max) => Math.min(Math.max(val, min), max);
const resolve = (val, ...args) =>
  typeof val === "function" ? val(...args) : val;

export default {
  name: "NuiDialog",
  props: {
    visible: { type: Boolean, default: false },
    // 有傳才會記住使用者調整後的尺寸與位置
    id: { type: String, default: "" },
    title: { type: String, default: "" },
    icon: { type: String, default: "bx bxs-grid-alt" },
    // number(px) 或 function，function 於開啟時才計算
    width: { type: [Number, Function], default: 480 },
    height: { type: [Number, Function], default: 400 },
    minWidth: { type: Number, default: 320 },
    minHeight: { type: Number, default: 200 },
    maxWidth: { type: Number, default: Infinity },
    maxHeight: { type: Number, default: Infinity },
    // { x, y } 或 function({ width, height, closeSpace })，未傳則置中
    // closeSpace：下方關閉鈕佔用的高度，彈窗要貼齊某元素上方時需扣除
    initialPosition: { type: [Object, Function], default: null },
    draggable: { type: Boolean, default: true },
    resizable: { type: Boolean, default: true },
    showClose: { type: Boolean, default: true },
    closeOnEsc: { type: Boolean, default: true },
    appendToBody: { type: Boolean, default: true },
    destroyOnClose: { type: Boolean, default: false },
  },
  data() {
    return {
      x: 0,
      y: 0,
      w: 0,
      h: 0,
      z: 0,
      directions: ["n", "s", "e", "w", "ne", "nw", "se", "sw"],
      pointer: null,
      frame: null,
    };
  },
  computed: {
    dialogStyle() {
      return {
        left: `${this.x}px`,
        top: `${this.y}px`,
        width: `${this.w}px`,
        height: `${this.h}px`,
        zIndex: this.z,
      };
    },
    storageKey() {
      const userId = this.$store.state.account.AC_userdata.id;
      return this.id && userId ? `${STORAGE_PREFIX}${userId}` : "";
    },
    closeSpace() {
      return this.showClose ? CLOSE_SPACE : 0;
    },
    layout() {
      return { x: this.x, y: this.y, width: this.w, height: this.h };
    },
  },
  watch: {
    visible(val) {
      val ? this.onOpen() : this.onClose();
    },
  },
  mounted() {
    if (this.appendToBody) document.body.appendChild(this.$el);
    if (this.visible) this.onOpen();
  },
  beforeDestroy() {
    if (this.visible) this.cleanup();
    if (this.appendToBody && this.$el.parentNode) {
      this.$el.parentNode.removeChild(this.$el);
    }
  },
  methods: {
    close() {
      this.$emit("update:visible", false);
    },
    resetLayout() {
      if (this.storageKey) {
        const layouts = this.readLayouts();
        delete layouts[this.id];
        this.writeLayouts(layouts);
      }
      this.applyLayout(this.getDefaultLayout());
    },
    onOpen() {
      this.applyLayout(this.loadLayout() || this.getDefaultLayout());
      this.bringToFront();
      openDialogs.push(this);
      window.addEventListener("resize", this.onWindowResize);
      document.addEventListener("keydown", this.onKeydown);
      this.$emit("open");
    },
    onClose() {
      this.cleanup();
      this.$emit("close");
    },
    cleanup() {
      this.stopPointer();
      const idx = openDialogs.indexOf(this);
      if (idx > -1) openDialogs.splice(idx, 1);
      window.removeEventListener("resize", this.onWindowResize);
      document.removeEventListener("keydown", this.onKeydown);
    },
    bringToFront() {
      // 與 Element 共用 z-index 計數，下拉選單、tooltip 才會疊在彈窗之上
      if (this.z && this.z === PopupManager.zIndex - 1) return;
      this.z = PopupManager.nextZIndex();
    },
    onKeydown(e) {
      if (e.key !== "Escape" || !this.closeOnEsc) return;
      const top = openDialogs.reduce((a, b) => (b.z > a.z ? b : a));
      if (top === this) this.close();
    },
    onWindowResize() {
      this.applyLayout(this.layout);
    },

    // layout
    getViewport() {
      return { vw: window.innerWidth, vh: window.innerHeight };
    },
    getSizeLimits() {
      const { vw, vh } = this.getViewport();
      const maxW = Math.min(this.maxWidth, vw);
      const maxH = Math.min(this.maxHeight, vh - this.closeSpace);
      return {
        minW: Math.min(this.minWidth, maxW),
        minH: Math.min(this.minHeight, maxH),
        maxW,
        maxH,
      };
    },
    getDefaultLayout() {
      const { minW, minH, maxW, maxH } = this.getSizeLimits();
      const width = clamp(resolve(this.width), minW, maxW);
      const height = clamp(resolve(this.height), minH, maxH);
      const pos = resolve(this.initialPosition, {
        width,
        height,
        closeSpace: this.closeSpace,
      });
      if (pos) return { x: pos.x, y: pos.y, width, height };

      const { vw, vh } = this.getViewport();
      return {
        x: (vw - width) / 2,
        y: (vh - this.closeSpace - height) / 2,
        width,
        height,
      };
    },
    applyLayout({ x, y, width, height }) {
      const { vw, vh } = this.getViewport();
      const { minW, minH, maxW, maxH } = this.getSizeLimits();
      this.w = clamp(width, minW, maxW);
      this.h = clamp(height, minH, maxH);
      this.x = clamp(x, 0, vw - this.w);
      this.y = clamp(y, 0, vh - this.closeSpace - this.h);
    },
    readLayouts() {
      try {
        const layouts = JSON.parse(localStorage.getItem(this.storageKey));
        return layouts && typeof layouts === "object" ? layouts : {};
      } catch (e) {
        return {};
      }
    },
    writeLayouts(layouts) {
      try {
        if (Object.keys(layouts).length) {
          localStorage.setItem(this.storageKey, JSON.stringify(layouts));
        } else {
          localStorage.removeItem(this.storageKey);
        }
      } catch (e) {
        // localStorage 不可用時忽略
      }
    },
    loadLayout() {
      if (!this.storageKey) return null;
      const layout = this.readLayouts()[this.id];
      const valid =
        layout &&
        ["x", "y", "width", "height"].every((k) => Number.isFinite(layout[k]));
      return valid ? layout : null;
    },
    saveLayout() {
      if (!this.storageKey) return;
      this.writeLayouts({ ...this.readLayouts(), [this.id]: this.layout });
    },

    // drag & resize
    onHeaderPointerdown(e) {
      if (!this.draggable || e.target.closest(NO_DRAG_SELECTOR)) return;
      this.startPointer(e, "move");
    },
    onHeaderDblclick(e) {
      if (e.target.closest(NO_DRAG_SELECTOR)) return;
      this.resetLayout();
    },
    startPointer(e, mode) {
      if (e.button !== 0) return;
      e.preventDefault();
      this.pointer = {
        mode,
        startX: e.clientX,
        startY: e.clientY,
        start: this.layout,
        last: e,
        target: e.currentTarget,
      };
      // 捕捉 pointer：滑鼠移出元素時游標仍維持該元素的 cursor
      e.currentTarget.setPointerCapture(e.pointerId);
      document.body.classList.add("nui-dialog-pointer-active");
      window.addEventListener("pointermove", this.onPointerMove);
      window.addEventListener("pointerup", this.onPointerUp);
      window.addEventListener("pointercancel", this.onPointerUp);
    },
    onPointerMove(e) {
      const p = this.pointer;
      if (!p) return;
      p.last = e;
      if (!this.frame) this.frame = requestAnimationFrame(this.applyPointer);
    },
    applyPointer() {
      this.frame = null;
      const p = this.pointer;
      if (!p) return;
      const dx = p.last.clientX - p.startX;
      const dy = p.last.clientY - p.startY;
      if (p.mode === "move") {
        this.applyLayout({ ...p.start, x: p.start.x + dx, y: p.start.y + dy });
      } else {
        this.resize(p.mode, dx, dy, p.start);
      }
    },
    resize(direction, dx, dy, start) {
      const { vw, vh } = this.getViewport();
      const { minW, minH, maxW, maxH } = this.getSizeLimits();
      let { x, y, width, height } = start;

      if (direction.includes("e")) {
        width = clamp(start.width + dx, minW, Math.min(maxW, vw - start.x));
      }
      if (direction.includes("w")) {
        width = clamp(
          start.width - dx,
          minW,
          Math.min(maxW, start.x + start.width)
        );
        x = start.x + start.width - width;
      }
      if (direction.includes("s")) {
        height = clamp(
          start.height + dy,
          minH,
          Math.min(maxH, vh - this.closeSpace - start.y)
        );
      }
      if (direction.includes("n")) {
        height = clamp(
          start.height - dy,
          minH,
          Math.min(maxH, start.y + start.height)
        );
        y = start.y + start.height - height;
      }
      this.x = x;
      this.y = y;
      this.w = width;
      this.h = height;
    },
    onPointerUp() {
      const p = this.pointer;
      if (!p) return;
      if (this.frame) cancelAnimationFrame(this.frame);
      this.applyPointer();
      this.stopPointer();
      this.saveLayout();
      this.$emit(p.mode === "move" ? "drag-end" : "resize-end", this.layout);
    },
    stopPointer() {
      if (this.frame) cancelAnimationFrame(this.frame);
      this.frame = null;
      const p = this.pointer;
      if (!p) return;
      if (p.target.hasPointerCapture(p.last.pointerId)) {
        p.target.releasePointerCapture(p.last.pointerId);
      }
      this.pointer = null;
      document.body.classList.remove("nui-dialog-pointer-active");
      window.removeEventListener("pointermove", this.onPointerMove);
      window.removeEventListener("pointerup", this.onPointerUp);
      window.removeEventListener("pointercancel", this.onPointerUp);
    },
  },
};
</script>

<style lang="scss" scoped>
.nui-dialog {
  position: fixed;
  display: flex;
  flex-direction: column;
  gap: $nui-dialog-gap;
  padding: $nui-dialog-padding;
  background: $nui-dialog-bg;
  border-radius: $nui-dialog-radius;
  box-shadow: $nui-dialog-shadow;
  font-family: $nui-font-family;
  color: $nui-dialog-text-color;
  font-size: $nui-dialog-font-size;
  line-height: normal;

  &__header {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    gap: $nui-dialog-header-gap;
    min-height: $nui-dialog-header-height;
    user-select: none;
    &.is-draggable {
      cursor: move;
      touch-action: none;
    }
  }
  &__icon {
    font-size: $nui-dialog-icon-size;
    color: $nui-dialog-icon-color;
  }
  &__title {
    flex: 1;
    min-width: 0;
    font-size: $nui-dialog-title-font-size;
    font-weight: $nui-dialog-title-font-weight;
    color: $nui-dialog-title-color;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__body {
    flex: 1;
    min-height: 0;
    overflow: auto;
    @include nui-scrollbar;
  }

  &__footer {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: $nui-dialog-footer-gap;
    min-height: $nui-dialog-footer-height + $nui-dialog-gap;
    padding-top: $nui-dialog-gap;
    border-top: 1px $nui-dialog-footer-divider-style
      $nui-dialog-footer-divider-color;
  }

  &__close {
    position: absolute;
    top: calc(100% + #{$nui-dialog-close-offset});
    left: 50%;
    transform: translateX(-50%);
    display: flex;
    align-items: center;
    justify-content: center;
    width: $nui-dialog-close-size;
    height: $nui-dialog-close-size;
    padding: 0;
    border: none;
    border-radius: $nui-dialog-close-radius;
    background: $nui-dialog-close-bg;
    box-shadow: $nui-dialog-close-shadow;
    color: $nui-dialog-close-icon-color;
    font-size: $nui-dialog-close-icon-size;
    cursor: pointer;
  }

  &__resizer {
    position: absolute;
    z-index: 1;
    touch-action: none;
    &.is-n,
    &.is-s {
      left: 8px;
      right: 8px;
      height: 6px;
      cursor: ns-resize;
    }
    &.is-n {
      top: -3px;
    }
    &.is-s {
      bottom: -3px;
    }
    &.is-e,
    &.is-w {
      top: 8px;
      bottom: 8px;
      width: 6px;
      cursor: ew-resize;
    }
    &.is-e {
      right: -3px;
    }
    &.is-w {
      left: -3px;
    }
    &.is-ne,
    &.is-nw,
    &.is-se,
    &.is-sw {
      width: 12px;
      height: 12px;
    }
    &.is-ne {
      top: -4px;
      right: -4px;
      cursor: nesw-resize;
    }
    &.is-sw {
      bottom: -4px;
      left: -4px;
      cursor: nesw-resize;
    }
    &.is-nw {
      top: -4px;
      left: -4px;
      cursor: nwse-resize;
    }
    &.is-se {
      // 涵蓋右下角 icon 範圍
      right: -4px;
      bottom: -4px;
      width: 20px;
      height: 20px;
      cursor: nwse-resize;
    }
  }
  &__resize-icon {
    position: absolute;
    right: 4px;
    bottom: 4px;
    width: 24px;
    height: 24px;
    fill: none;
    stroke: $nui-dialog-resize-icon-color;
    stroke-width: 1;
    shape-rendering: crispEdges;
    pointer-events: none;
  }
}
</style>

<style lang="scss">
// 拖曳／縮放中避免選取文字
body.nui-dialog-pointer-active,
body.nui-dialog-pointer-active * {
  user-select: none !important;
}
</style>
